/**
 * Inner Banner Component
 * Handles autoplay videos in banner-inner sections
 * Supports MP4, YouTube, and Vimeo videos
 */
export function innerBanner() {
    const sections = document.querySelectorAll('.banner-inner[data-video-type]');
    
    if (sections.length === 0) return;

    sections.forEach(section => {
        const videoType = section.getAttribute('data-video-type');
        const videoSrc = section.getAttribute('data-video-src');
        const videoId = section.getAttribute('data-video-id');
        const videoContainer = section.querySelector('.banner-inner__video') || section;

        if (!videoType) return;

        const videoTypeLower = videoType.toLowerCase();

        switch (videoTypeLower) {
            case 'mp4':
                if (!videoSrc) return;
                initializeMP4Video(videoSrc, videoContainer);
                break;

            case 'youtube':
                if (!videoId) return;
                initializeYouTubeVideo(videoId, videoContainer);
                break;

            case 'vimeo':
                if (!videoId) return;
                initializeVimeoVideo(videoId, videoContainer);
                break;
        }
    });
}

/**
 * Initialize MP4 video with autoplay
 */
function initializeMP4Video(videoSrc, container) {
    let video = container.querySelector('.banner-inner__video-element');
    
    if (!video) {
        video = document.createElement('video');
        video.className = 'banner-inner__video-element';
        container.appendChild(video);
    }

    video.src = videoSrc;
    video.autoplay = true;
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.controls = false;
    video.preload = 'auto';

    // Play video when ready
    video.addEventListener('loadedmetadata', () => {
        video.play().catch(err => {
            console.warn('Autoplay prevented:', err);
            // Try to play after user interaction
            document.addEventListener('click', () => {
                video.play().catch(() => {});
            }, { once: true });
        });
    });

    // Ensure video plays if already loaded
    if (video.readyState >= 2) {
        video.play().catch(err => {
            console.warn('Autoplay prevented:', err);
        });
    }
}

/**
 * Initialize YouTube video with autoplay
 */
function initializeYouTubeVideo(videoId, container) {
    const origin = window.location.origin;

    // Try to use YouTube IFrame API if available
    if (typeof YT !== 'undefined' && YT.Player) {
        createYouTubePlayer(videoId, container);
    } else {
        // Load YouTube IFrame API if not already loaded
        if (typeof YT === 'undefined') {
            if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
                const tag = document.createElement('script');
                tag.src = 'https://www.youtube.com/iframe_api';
                const firstScriptTag = document.getElementsByTagName('script')[0];
                firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
            }

            // Wait for API to load
            const originalCallback = window.onYouTubeIframeAPIReady;
            window.onYouTubeIframeAPIReady = () => {
                if (originalCallback) originalCallback();
                createYouTubePlayer(videoId, container);
            };
        } else {
            // Fallback: use direct iframe embed with autoplay
            const iframe = document.createElement('iframe');
            iframe.className = 'banner-inner__youtube-player';
            iframe.src = `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1&enablejsapi=1&origin=${origin}`;
            iframe.allow = 'autoplay; encrypted-media';
            iframe.allowFullscreen = false;
            container.appendChild(iframe);
        }
    }
}

/**
 * Create YouTube player with autoplay
 */
function createYouTubePlayer(videoId, container) {
    const origin = window.location.origin;
    const playerDiv = document.createElement('div');
    playerDiv.className = 'banner-inner__youtube-player';
    playerDiv.id = 'youtube-player-banner-inner-' + Date.now();
    container.appendChild(playerDiv);

    const player = new YT.Player(playerDiv.id, {
        videoId: videoId,
        playerVars: {
            autoplay: 1, // Autoplay enabled
            mute: 1,
            loop: 1,
            playlist: videoId,
            controls: 0,
            showinfo: 0,
            rel: 0,
            modestbranding: 1,
            playsinline: 1,
            enablejsapi: 1,
            origin: origin
        },
        events: {
            onReady: (event) => {
                // Ensure video plays
                event.target.playVideo();
            },
            onError: (event) => {
                console.warn('YouTube player error:', event.data);
            }
        }
    });
}

/**
 * Initialize Vimeo video with autoplay
 */
function initializeVimeoVideo(videoId, container) {
    const iframe = document.createElement('iframe');
    iframe.className = 'banner-inner__vimeo-player';
    iframe.src = `https://player.vimeo.com/video/${videoId}?autoplay=1&muted=1&loop=1&background=1&controls=0&responsive=1`;
    iframe.allow = 'autoplay; fullscreen';
    iframe.allowFullscreen = false;
    container.appendChild(iframe);

    // Load Vimeo Player API for additional control if needed
    if (typeof Vimeo === 'undefined') {
        const tag = document.createElement('script');
        tag.src = 'https://player.vimeo.com/api/player.js';
        tag.onload = () => {
            if (iframe && typeof Vimeo !== 'undefined') {
                try {
                    const player = new Vimeo.Player(iframe);
                    player.play().catch(err => {
                        console.warn('Vimeo autoplay prevented:', err);
                    });
                } catch (err) {
                    console.warn('Error initializing Vimeo player:', err);
                }
            }
        };
        document.head.appendChild(tag);
    } else {
        try {
            const player = new Vimeo.Player(iframe);
            player.play().catch(err => {
                console.warn('Vimeo autoplay prevented:', err);
            });
        } catch (err) {
            console.warn('Error initializing Vimeo player:', err);
        }
    }
}
